// Client-side mirror of backend/uploadPolicy.ts — keep both in sync. This copy
// only provides early feedback and file-picker filters; the backend is the one
// that actually enforces the allowlist.
export const ALLOWED_UPLOAD_TYPES: Readonly<Record<string, readonly string[]>> = {
  // Images (SVG intentionally excluded — it can embed script). Legacy aliases
  // like image/pjpeg / image/x-png are what some Windows MIME registries report.
  png: ['image/png', 'image/x-png'],
  jpg: ['image/jpeg', 'image/pjpeg'],
  jpeg: ['image/jpeg', 'image/pjpeg'],
  webp: ['image/webp'],
  gif: ['image/gif'],
  bmp: ['image/bmp', 'image/x-ms-bmp'],
  tif: ['image/tiff'],
  tiff: ['image/tiff'],
  avif: ['image/avif'],
  // Video
  mp4: ['video/mp4'],
  m4v: ['video/x-m4v', 'video/mp4'],
  mov: ['video/quicktime'],
  webm: ['video/webm'],
  mkv: ['video/x-matroska'],
  avi: ['video/x-msvideo', 'video/avi'],
  // Audio
  mp3: ['audio/mpeg', 'audio/mp3'],
  wav: ['audio/wav', 'audio/x-wav', 'audio/wave', 'audio/vnd.wave'],
  flac: ['audio/flac', 'audio/x-flac'],
  ogg: ['audio/ogg'],
  m4a: ['audio/mp4', 'audio/x-m4a'],
  aac: ['audio/aac', 'audio/x-aac', 'audio/vnd.dlna.adts'],
  // Text
  txt: ['text/plain'],
  // DaVinci Resolve grade still
  drx: ['application/octet-stream'],
  // Photoshop
  psd: [
    'image/vnd.adobe.photoshop',
    'application/x-photoshop',
    'application/photoshop',
    'application/psd',
    'application/octet-stream',
  ],
};

export type UploadKind = 'image' | 'video' | 'audio';

const KIND_EXTENSIONS: Record<UploadKind, string[]> = {
  image: ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'tif', 'tiff', 'avif', 'psd'],
  video: ['mp4', 'm4v', 'mov', 'webm', 'mkv', 'avi'],
  audio: ['mp3', 'wav', 'flac', 'ogg', 'm4a', 'aac'],
};

const toAccept = (extensions: string[]): string => extensions.map((e) => `.${e}`).join(',');

// `accept` attribute value for file pickers — every allowed type, or one kind.
export const acceptFor = (kind?: UploadKind): string =>
  toAccept(kind ? KIND_EXTENSIONS[kind] : Object.keys(ALLOWED_UPLOAD_TYPES));

const getExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(lastDot + 1).toLowerCase() : '';
};

// Whether a file belongs to the given kind (by extension, since browsers may
// report an empty MIME type for some allowed formats).
export const matchesKind = (file: File, kind: UploadKind): boolean => KIND_EXTENSIONS[kind].includes(getExtension(file.name));

export type UploadValidation = { ok: true; contentType: string } | { ok: false; error: string };

/**
 * Check a file against the allowlist and resolve the content type to declare.
 * Browsers often report an empty type for less common formats (notably .psd),
 * so in that case we fall back to the first MIME listed for the extension.
 */
export const validateUpload = (file: File): UploadValidation => {
  const extension = getExtension(file.name);
  const allowedMimes = extension ? ALLOWED_UPLOAD_TYPES[extension] : undefined;
  if (!allowedMimes) {
    return {
      ok: false,
      error: `${file.name}: file type not allowed. Allowed: images, videos, audio, .txt, .psd and .drx`,
    };
  }
  const declared = file.type.split(';')[0].trim().toLowerCase();
  if (!declared) return { ok: true, contentType: allowedMimes[0] };
  if (!allowedMimes.includes(declared)) {
    return { ok: false, error: `${file.name}: content type "${file.type}" does not match a .${extension} file` };
  }
  return { ok: true, contentType: file.type };
};
